import { Injectable } from '@angular/core';
import { TimeOfDayHelper } from '@app/core/helpers/time-of-day.helper';

/**
 * Warms the large hero images of the main screens into the browser (and, in production,
 * the service-worker) cache so navigating between screens paints instantly instead of
 * downloading + decoding a multi-megabyte PNG on arrival.
 *
 * The asset paths mirror those declared in the hero components
 * (landing-hero, plan-home-hero, sanctuary-hero). Kept in sync by hand — if a hero's
 * src changes, update the matching entry here.
 */
@Injectable({ providedIn: 'root' })
export class ImagePreloadService {
  private warmed = false;

  /**
   * Preload the hero imagery for the user's device class, sequentially so it never
   * contends with the initial page load. Safe to call more than once — only runs once.
   */
  warmHeroImages(): void {
    if (this.warmed || typeof window === 'undefined') {
      return;
    }
    this.warmed = true;

    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    const tod = TimeOfDayHelper.getTimeOfDay();

    const sky: Record<string, string> = {
      morning: 'assets/sanctuary/Homepage Hero - Morning Sky.webp',
      afternoon: 'assets/sanctuary/Homepage Hero - Afternoon Sky.webp',
      evening: 'assets/sanctuary/Homepage Hero - Evening Sky.webp',
    };
    const mobile: Record<string, string> = {
      morning: 'assets/sanctuary/Homepage Hero - Morning - Mobile.webp',
      afternoon: 'assets/sanctuary/Homepage Hero - Afternoon - Mobile.webp',
      evening: 'assets/sanctuary/Homepage Hero - Evening - Mobile.webp',
    };

    const urls = isDesktop
      ? [
          'assets/plan/plan-hero-desktop.webp',
          'assets/sanctuary/sanctuary-bg.webp',
          'assets/sanctuary/sanctuary-doodat.webp',
          sky[tod],
          'assets/sanctuary/Homepage Hero - Desktop.webp',
        ]
      : [
          'assets/plan/plan-hero-mobile.webp',
          'assets/sanctuary/sanctuary-bg.webp',
          'assets/sanctuary/sanctuary-doodat.webp',
          mobile[tod],
        ];

    this.preloadSequential(urls);
  }

  /** Load images one after another so the warm-up stays a low-priority background task. */
  private preloadSequential(urls: string[]): void {
    const next = (i: number): void => {
      if (i >= urls.length) {
        return;
      }
      const img = new Image();
      const advance = () => next(i + 1);
      img.onload = advance;
      img.onerror = advance;
      img.src = urls[i];
    };
    next(0);
  }
}
